import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Chip,
  Alert,
  Fade,
  LinearProgress,
  TablePagination,
  IconButton,
  Collapse,
  Typography,
  CircularProgress,
  Divider
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ClearAll as ClearAllIcon,
  FileDownload as FileDownloadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from "@mui/icons-material";
import { useSearchHistory } from "../hooks/useSearchHistory";
import { useHistoryHandler } from '../hooks/useHistoryHandler';
import SearchHistory from "../components/SearchHistory";
import { useApiData } from "../hooks/useApiData";
import { useFilters } from "../hooks/useFilters";
import CompanyCards from "../components/CompanyCards";
import axios from 'axios';
import api from '../utils/apiInterceptor';
import { API_URL } from '../constants/data';

const Row = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const { filters } = useFilters();

  const handleExpand = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen && subCategories.length === 0) {
      setLoading(true);
      setCompanyLoading(true);
      try {
        const prompt = row.prompt || row.categoryName;

        const start_date = filters?.startDate
          ? new Date(filters.startDate).toISOString().split('T')[0]
          : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
        const end_date = filters?.endDate
          ? new Date(filters.endDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0];
        const value = filters?.value || 1;
        const threshold = filters?.threshold || 1.3;
        const accessToken = localStorage.getItem('access_token') || '';

        // ── Step 1: Call Semantic RCA to get fresh complaintNumbers ──
        let resolvedComplaintNumbers = ['NA'];
        try {
          console.log("Semantic RCA API called", filters);
          const rcaRes = await api.post(`${API_URL}/get_semantic_rca`, {
            query: prompt,
            start_date,
            end_date,
            threshold,
            value,
            purchaseCity: filters?.CityName || 'All',
            stateName: filters?.stateName || 'All',
            complaintType: filters?.complaintType || 'All',
            complaintMode: filters?.complaintMode || 'All',
            companyName: filters?.companyName || 'All',
            complaintStatus: filters?.complaintStatus || 'All',
            complainNumbers: row.complaintNumbers || ['NA'],
          });
          if (rcaRes.data?.complaintNumbers?.length > 0) {
            resolvedComplaintNumbers = rcaRes.data.complaintNumbers;
          }
        } catch (rcaErr) {
          console.warn("RCA pre-fetch failed, proceeding with ['NA']:", rcaErr);
        }

        // ── Step 2: Call subcategory_with_counts + get_all_company_distribution in parallel ──
        const [subRes, companyRes] = await Promise.allSettled([
          axios.post(
            `${API_URL}/subcategory_with_counts`,
            {
              input_prompt: prompt,
              value,
              start_date,
              end_date,
              threshold,
              purchaseCity: filters?.CityName || 'All',
              stateName: filters?.stateName || 'All',
              complaintType: filters?.complaintType || 'All',
              complaintMode: filters?.complaintMode || 'All',
              companyName: filters?.companyName || 'All',
              complaintStatus: filters?.complaintStatus || 'All',
              complainNumbers: resolvedComplaintNumbers,
              max_retries: 3,
            },
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              timeout: 30000,
            }
          ),
          api.post(`${API_URL}/get_all_company_distribution`, {
            query: prompt,
            start_date,
            end_date,
            purchaseCity: filters?.CityName || 'All',
            stateName: filters?.stateName || 'All',
            complaintMode: filters?.complaintMode || 'All',
            complaintStatus: filters?.complaintStatus || 'All',
            value,
            companyName: filters?.companyName || 'All',
            threshold,
            complainNumbers: resolvedComplaintNumbers,
            skip: 0,
            size: 25,
          }),
        ]);

        // Handle subcategories result
        if (subRes.status === 'fulfilled') {
          const subList = subRes.value?.data?.subcategories || [];
          if (subList.length > 0) {
            const results = subList.map(item => ({
              name: item.subcategory,
              count: item.count || 0,
              percent: row.count > 0 ? ((item.count / row.count) * 100).toFixed(2) : "0.00"
            }));
            results.sort((a, b) => b.count - a.count);
            setSubCategories(results);
          }
        } else {
          console.error('Subcategories fetch failed:', subRes.reason);
        }

        // Handle company distribution result
        if (companyRes.status === 'fulfilled') {
          const data = companyRes.value?.data;
          if (data && (data.converged || data.non_converged)) {
            setCompanyData({
              converged: data.converged || [],
              non_converged: data.non_converged || []
            });
          } else if (data && Array.isArray(data.distribution)) {
            // Fallback: treat flat distribution as converged list
            setCompanyData({
              converged: data.distribution,
              non_converged: []
            });
          }
        } else {
          console.error('Company distribution fetch failed:', companyRes.reason);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        setCompanyLoading(false);
      }
    }
  };

  return (
    <>
      <TableRow
        sx={{
          "&:hover": { backgroundColor: "#f5f5f5" },
          "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
          cursor: 'pointer'
        }}
        onClick={handleExpand}
      >
        <TableCell sx={{ width: '50px' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleExpand();
            }}
          >
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.categoryName}
        </TableCell>
        <TableCell align="center">{row.percent}%</TableCell>
        <TableCell align="center">{row.count}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: 1, boxShadow: 'inset 0 0 5px rgba(0,0,0,0.05)' }}>

              {/* ── Subcategories Section ── */}
              <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 600, color: '#555' }}>
                Subcategories Breakdown
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Table size="small" aria-label="subcategories">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#666' }}>Subcategory Name</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#666' }}>% of Parent</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#666' }}>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subCategories.length > 0 ? (
                      subCategories.map((subRow, index) => (
                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell component="th" scope="row" sx={{ pl: 2 }}>
                            {subRow.name}
                          </TableCell>
                          <TableCell align="center">{subRow.percent}%</TableCell>
                          <TableCell align="center">{subRow.count}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ fontStyle: 'italic', color: '#888' }}>
                          No subcategories found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}

              {/* ── Company Distribution Section ── */}
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 600, color: '#555', mb: 1.5 }}>
                  Company Analysis — <em style={{ fontWeight: 400, color: '#888' }}>{row.categoryName}</em>
                </Typography>
                <Paper
                  elevation={1}
                  sx={{
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    border: '1px solid rgba(0,0,0,0.06)'
                  }}
                >
                  <CompanyCards
                    companyData={companyData}
                    isLoading={companyLoading}
                    onCompanyClick={(company, type) => {
                      console.log('Company clicked in category expand:', company, type);
                    }}
                  />
                </Paper>
              </Box>

            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CategoryPage = () => {
  const [categories, setCategories] = useState({});
  const [totalComplaints, setTotalComplaints] = useState(0);
  const [loading, setLoading] = useState(true);

  // UI states
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("count");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const {
    setTotalCounts,
    fetchSemanticRCA,
    fetchUserData,
    fetchSpatialData,
    setIsLoading,
  } = useApiData();
  const { searchHistory, saveToHistory, clearHistory, deleteHistoryItem, selectedHistoryIndex, setSelectedHistoryIndex } = useSearchHistory();

  const { handleHistoryClick } = useHistoryHandler({
    fetchSemanticRCA,
    fetchSpatialData,
    fetchUserData,
    setTotalCounts,
    setIsLoading
  });

  useEffect(() => {
    setLoading(true);
    try {
      let cacheKey = "categoryDataCache";
      if (searchHistory && searchHistory.length > 0) {
        cacheKey = "currentSubcategoryData";
      }

      const cached = JSON.parse(localStorage.getItem(cacheKey) || "{}");
      const data = cached?.data || {};
      const cats = data.categories || {};
      console.log({ cats })
      const total = data.totalComplaints || data.totalCount || 0;
      setCategories(cats);
      setTotalComplaints(total);
    } catch (err) {
      console.error("Error loading cached data:", err);
    } finally {
      setLoading(false);
    }
  }, [searchHistory]);

  // Convert categories object into array for table
  const categoryRows = useMemo(() => {
    if (!categories || Object.keys(categories).length === 0) return [];
    return Object.entries(categories)
      .filter(([name]) => name && name.trim() !== "") // Filter out empty names
      .map(([name, info]) => ({
        categoryName: name,
        count: info.count || 0,
        prompt: info.prompt || name, // Include prompt, fallback to name
        percent:
          totalComplaints > 0
            ? ((info.count / totalComplaints) * 100).toFixed(2)
            : "0.00",
        complaintNumbers: info.complaintNumbers || []
      }));
  }, [categories, totalComplaints]);

  // Filter based on search
  const filteredRows = useMemo(() => {
    if (!searchTerm) return categoryRows;
    return categoryRows.filter((row) =>
      row.categoryName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categoryRows, searchTerm]);

  // Sorting
  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (orderBy === "percent" || orderBy === "count") {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      } else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }
      if (order === "asc") return aVal > bVal ? 1 : -1;
      else return aVal < bVal ? 1 : -1;
    });
    return sorted;
  }, [filteredRows, orderBy, order]);

  // Pagination
  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedRows.slice(start, end);
  }, [sortedRows, page, rowsPerPage]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    const csv = [
      ['Category Name', 'Complaints Percentage', 'Complaints Count'],
      ...sortedRows.map(row => [
        row.categoryName,
        row.percent,
        row.count
      ])
    ].map(row => row.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'categories.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ p: 2 }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >

          <SearchHistory
            searchHistory={searchHistory}
            onHistoryClick={handleHistoryClick}
            onClearHistory={clearHistory}
            onDeleteHistoryItem={deleteHistoryItem}
            selectedHistoryIndex={selectedHistoryIndex}
            setSelectedHistoryIndex={setSelectedHistoryIndex}
          />
        </Box>

        {categoryRows.length === 0 && !loading && (
          <Alert severity="info">No category data found in cache.</Alert>
        )}

        <TableContainer component={Paper}>
          {/* Unified Control Bar */}
          <Box sx={{
            p: { xs: 1.5, sm: 2 },
            display: 'flex',
            gap: { xs: 1, sm: 1.5, md: 2 },
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}>
            {/* Search Bar */}
            <TextField
              placeholder="Search category by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                flexGrow: 1,
                minWidth: { xs: '100%', sm: '200px', lg: '250px' },
                order: { xs: 1, lg: 1 },
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                flexShrink: 0,
                order: { xs: 2, lg: 2 },
                minWidth: { xs: '100%', sm: 'auto' },
                borderRadius: '4px',
                backgroundColor: '#fff',
                overflow: 'hidden',
                '& .MuiTablePagination-toolbar': {
                  minHeight: '38px',
                  height: '38px',
                  padding: 0,
                  paddingLeft: { xs: 1, sm: 1.5 },
                  paddingRight: { xs: 0.5, sm: 1 },
                },
                '& .MuiTablePagination-spacer': {
                  flex: '0 0 auto',
                },
                '& .MuiTablePagination-selectLabel': {
                  margin: 0,
                  fontSize: '0.875rem',
                  display: { xs: 'none', sm: 'block' },
                },
                '& .MuiTablePagination-select': {
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingLeft: '4px',
                  paddingRight: '4px',
                  fontSize: '0.875rem',
                },
                '& .MuiTablePagination-selectIcon': {
                  top: 'calc(50% - 12px)',
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0,
                  fontSize: '0.875rem',
                },
                '& .MuiTablePagination-actions': {
                  marginLeft: { xs: 0.5, sm: 1 },
                  '& .MuiIconButton-root': {
                    padding: '8px',
                  }
                }
              }}
            />

            {/* Export Button */}
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              sx={{
                flexShrink: 0,
                height: '40px',
                minWidth: { xs: '100%', sm: 'auto' },
                order: { xs: 3, lg: 3 },
                textTransform: 'none',
                borderColor: 'rgba(0, 0, 0, 0.23)',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              Export
            </Button>
          </Box>

          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                <TableCell />
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={true}
                    direction={orderBy === "categoryName" ? order : "desc"}
                    onClick={() => handleSort("categoryName")}
                  >
                    Category Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={true}
                    direction={orderBy === "percent" ? order : "desc"}
                    onClick={() => handleSort("percent")}
                  >
                    Category Complaint %
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={true}
                    direction={orderBy === "count" ? order : "desc"}
                    onClick={() => handleSort("count")}
                  >
                    Complaint Count
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.map((row, idx) => (
                <Row key={idx} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Fade >
  );
};

export default CategoryPage;
